import React from 'react';
import axios from '../../utils/axiosConfig';

import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Table from '../../components/Table';
import SelectBox from '../../components/SelectBox';
import Pagination from '../../components/Pagination';

// Security event severities → a colour, so a failed login / firewall change stands
// out from routine logins in the log.
const SEV_COLOR = {
	critical: '#c81212', high: '#c81212', medium: '#b45309', low: '#0f9d58', info: 'var(--grey)',
};
const sevColor = (s) => SEV_COLOR[String(s || '').toLowerCase()] || 'var(--grey)';

const ApplianceSecurityScreen = () => {
	const [selectedWatcher, setSelectedWatcher] = React.useState('');
	const [watchers, setWatchers] = React.useState([]);
	const [cols, setCols] = React.useState([]);
	const [rows, setRows] = React.useState([]);
	const [loading, setLoading] = React.useState(true);
	const [searchKey, setSearchKey] = React.useState('');
	const [pageNumber, setPageNumber] = React.useState(1);
	const pageLimit = 20;

	// Load the watcher dropdown once. No auto-select: the page renders with the
	// dropdown populated but no data until the user picks a watcher. Clear the
	// initial spinner here since the data effect early-returns while nothing is
	// selected.
	React.useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const { data } = await axios.get('/api/appliance-events');
				if (cancelled || !data.status) return;
				setWatchers(data.availableWatchers || data.watchers || []);
				setCols(data.NetCols || []);
			} catch (e) {
				/* leave dropdown empty on error */
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	// Load rows for the selected watcher — clear + guard so switching watchers
	// never shows the previous one's rows.
	React.useEffect(() => {
		if (!selectedWatcher) return;
		let cancelled = false;
		(async () => {
			setLoading(true);
			setRows([]);
			try {
				const fd = new FormData();
				fd.append('watchers', selectedWatcher);
				const { data } = await axios.post('/api/appliance-events', fd);
				if (cancelled) return;
				if (data.status) {
					setCols(data.NetCols || cols);
					setRows(data.NetRows || []);
				}
			} catch (e) {
				/* leave empty on error */
			} finally {
				if (!cancelled) { setLoading(false); setPageNumber(1); }
			}
		})();
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedWatcher]);

	const sevIdx = cols.findIndex((c) => c === 'Severity');

	const filtered = React.useMemo(() => {
		if (!searchKey) return rows;
		const q = searchKey.toLowerCase();
		return rows.filter((r) => r.some((cell) => cell && cell.toString().toLowerCase().includes(q)));
	}, [rows, searchKey]);

	const paged = React.useMemo(() => {
		const start = (pageNumber - 1) * pageLimit;
		return filtered.slice(start, start + pageLimit);
	}, [filtered, pageNumber]);

	return (
		<Content
			currentMenu='appliance-security'
			headerTitle='Appliance Security'
			searchBox={true}
			searchPlaceholder='Search events'
			searchValue={searchKey}
			searchOnChange={(e) => { setSearchKey(e.target.value); setPageNumber(1); }}
		>
			<div style={{ padding: '0 0 14px' }}>
				<SelectBox label='Select Watcher' value={selectedWatcher}
					onChange={(e) => setSelectedWatcher(e.target.value)}>
					<option value=''>Select a Watcher</option>
					<option value='all'>All Watchers</option>
					{watchers.map((w) => <option key={w[0]} value={w[0]}>{w[1] || w[0]}</option>)}
				</SelectBox>
			</div>

			{loading ? <Loader /> : (
				<>
					<Table
						containerStyle={{ height: '100%', flex: '1 1 auto', overflow: 'auto' }}
						tableHead={<tr>{cols.map((c, i) => <th key={c + i}>{c}</th>)}</tr>}
						tableRow={
							paged.length > 0 ? (
								paged.map((row, ri) => (
									<tr key={`ev-${ri}`}>
										{row.map((cell, ci) => (
											<td key={`ev-${ri}-${ci}`}
												style={ci === sevIdx ? { color: sevColor(cell), fontWeight: 600 } : {}}>
												{cell}
											</td>
										))}
									</tr>
								))
							) : (
								<tr><td colSpan={cols.length || 7} style={{ textAlign: 'center' }}>
									No appliance security events for the selected watcher.
								</td></tr>
							)
						}
					/>
					{filtered.length > pageLimit && (
						<div className='paginationContainer'>
							<Pagination
								onPageChange={(s) => setPageNumber(s.selected + 1)}
								rowsPerPage={pageLimit}
								pageNumber={pageNumber}
								totalPageSize={filtered.length}
							/>
						</div>
					)}
				</>
			)}
		</Content>
	);
};

export default ApplianceSecurityScreen;
